import React, { useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import { GridContainer, GridItem } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import DialogToolbarMenuItemDeactivate from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDeactivate';
import DialogToolbarMenuItemActivate from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemActivate';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogInfoDeactivated from '@/ui/Dialog/DialogInfoDeactivated';
import DialogInfoMeta from '@/ui/Dialog/DialogInfoMeta';
import InfoBox from '@/ui/Dialog/InfoBox';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { PickerItem } from '@/@types/controls/controls';

const PPZahtjeviDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { mode, onClose, title, level, allowPrimaryModelActivate = false, initialRecord } = props;

  const zahtjevGid = initialRecord?.zahtjev_gid as string | undefined;

  console.log(zahtjevGid)

  const {
    record,
    validation,
    fields,
    pickerDataControllers,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doRefresh,
    doActivate,
    doDeactivate,
  } = useFormController({
    ...(props as FormControllerProps),
    pickerApiPathParams: zahtjevGid ? { zahtjev_gid: zahtjevGid } : {},
  });

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = `${t('error.addRecord')} ${result.error}`;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = `${t('error.editRecord')} ${result.error}`;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then((resolve) => {
          if (resolve.success) {
            close({ dataChanged: false, action: 'cancel' });
          }
        })
        .catch((e) => console.error(e));
    }
  };

  const handleActivate = () => {
    if (doActivate) {
      doActivate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.activateRecord');
            showNotification(msg, 'success');
            doRefresh();
          } else if (result.error) {
            const msg = t('error.activateRecord') + result.error;
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleDeactivate = () => {
    if (doDeactivate) {
      doDeactivate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.deactivateRecord');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = t('error.deactivateRecord') + result.error;
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog(level);
  };

  let cmode = mode;
  if (record.status === 30 || (allowPrimaryModelActivate && record.active === false)) {
    cmode = 'view';
  }

  const zahtjevStatus = record.zahtjev_kc_status_id
    ? typeof record.zahtjev_kc_status_id === 'number'
      ? record.zahtjev_kc_status_id
      : (record.zahtjev_kc_status_id as PickerItem).value
    : null;

  const showInfoBox = zahtjevStatus && zahtjevStatus !== 20;

  return (
    <DynamicDialog open maxWidth="xl" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{title}</DialogToolbarHeading>
          <ToolbarFillContent />
          {allowPrimaryModelActivate && (
            <DialogToolbarMenuButton>
              {record.active ? (
                <DialogToolbarMenuItemDeactivate onClick={handleDeactivate} />
              ) : (
                <DialogToolbarMenuItemActivate onClick={handleActivate} />
              )}
            </DialogToolbarMenuButton>
          )}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer my={2} spacing={4}>

          {/* LEFT: čestica + vlasništvo info */}
          <GridItem size={{ xs: 12, md: 6 }}>
            <GridContainer>
              <FormContent
                title={t('pp:zahtjevi.sections.cestica') as string}
                fieldNames={['kc', 'ko_mb', 'ko_naziv', 'podnositelj_napomena']}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode={cmode}
                columns={2}
                pickerDataControllers={pickerDataControllers}
              />
              <FormContent
                title={t('pp:zahtjevi.sections.vlasnistvo') as string}
                fieldNames={['vlasnik', 'vlasnik_adresa', 'posjednik', 'posjednik_adresa']}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode={cmode}
                columns={2}
                pickerDataControllers={pickerDataControllers}
              />
              <FormContent
                fieldNames={['primjedba']}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode={cmode}
                columns={1}
                pickerDataControllers={pickerDataControllers}
              />
            </GridContainer>
          </GridItem>

          {/* RIGHT: statusi + ostalo */}
          <GridItem size={{ xs: 12, md: 6 }}>
            <GridContainer>
              <FormContent
                title={t('pp:zahtjevi.sections.statusi') as string}
                fieldNames={['kategorija_id', 'vlasnistvo_status_id', 'zahtjev_kc_status_id', 'is_provjereno']}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode={cmode}
                columns={2}
              />
              <FormContent
                title={t('pp:zahtjevi.sections.ostalo') as string}
                fieldNames={['dokumenti_suglasnosti']}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode={cmode}
                columns={1}
                pickerDataControllers={pickerDataControllers}
              />
              {showInfoBox
                ? (record.kriteriji_odbijanja as { ttoken: string; type: 'CRIT' | 'WARN' | 'NOTE' }[]).map((kriterij) => {
                    const color = kriterij.type === 'CRIT' ? 'error' : kriterij.type === 'WARN' ? 'warning' : 'info';
                    const icon = kriterij.type === 'CRIT' ? 'error' : kriterij.type === 'WARN' ? 'warning' : 'info';
                    return (
                      <GridItem key={`${kriterij.ttoken}-${kriterij.type}`} size={12}>
                        <InfoBox color={color} icon={icon} labelToken={kriterij.ttoken} />
                      </GridItem>
                    );
                  })
                : null}
            </GridContainer>
          </GridItem>

        </GridContainer>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          {mode === 'update' && allowPrimaryModelActivate && !record.active ? (
            <DialogInfoDeactivated />
          ) : (
            <DialogInfoMeta record={record} />
          )}
        </Box>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' && (!allowPrimaryModelActivate || record.active === true) ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default PPZahtjeviDialog;
